import { AdvancedWidgetConfig } from "types/WidgetTypes";

/*
 * dashboardConfig.ts
 * Unified config of every widget which can appear on the dashboard,
 * both the default widgets every customer sees and the opt-in
 * advanced widgets. They share one grid and one catalog so the
 * default widgets can also be toggled off, moved and resized.
 *
 * Grid size height values are in internal rows, 2 rows per conceptual
 * height unit, so a conceptual height of 1.5 is possible as 3 rows.
 * A conceptual height of 1 (2 rows) renders at roughly 138px, sized
 * to fit a number tile whose title and difference line both wrap.
 */

// Display labels for the job status breakdown bar buckets
export const JOB_STATUS_BREAKDOWN_LABELS: Record<string, string> = {
    to_schedule: 'dashboard.chart.job_status_breakdown.to_schedule',
    ordered: 'dashboard.chart.job_status_breakdown.ordered',
    reserved: 'dashboard.chart.job_status_breakdown.reserved',
    scheduled: 'dashboard.chart.job_status_breakdown.scheduled',
    en_route: 'dashboard.chart.job_status_breakdown.en_route',
    in_progress: 'dashboard.chart.job_status_breakdown.in_progress',
    incomplete: 'dashboard.chart.job_status_breakdown.incomplete',
    completed: 'dashboard.chart.job_status_breakdown.completed',
};

// Display labels for the proposal status breakdown bar buckets
export const PROPOSAL_STATUS_LABELS: Record<string, string> = {
    draft: 'dashboard.advanced_widget.proposal_status.draft',
    sent: 'dashboard.advanced_widget.proposal_status.sent',
    expired: 'dashboard.advanced_widget.proposal_status.expired',
    accepted: 'dashboard.advanced_widget.proposal_status.accepted',
    rejected: 'dashboard.advanced_widget.proposal_status.rejected',
};

// Colors for the proposal status breakdown bar, matching the status badge colors used in the proposal list
export const PROPOSAL_STATUS_COLORS: Record<string, string> = {
    draft: 'var(--general-light-gray)',
    sent: 'var(--general-yellow)',
    expired: 'var(--general-red)',
    accepted: 'var(--general-green)',
    rejected: 'var(--general-red)',
};

// Display labels for the project billing type breakdown donut
export const PROJECT_BILLING_TYPE_LABELS: Record<string, string> = {
    calculation: 'dashboard.advanced_widget.project_billing_type.calculation',
    fixed: 'dashboard.advanced_widget.project_billing_type.fixed',
    non_billing: 'dashboard.advanced_widget.project_billing_type.non_billing',
};

// Display labels for the invoice status breakdown bar buckets
export const INVOICE_STATUS_LABELS: Record<string, string> = {
    draft: 'dashboard.advanced_widget.invoice_status.draft',
    open: 'dashboard.advanced_widget.invoice_status.open',
    overdue: 'dashboard.advanced_widget.invoice_status.overdue',
    reminded: 'dashboard.advanced_widget.invoice_status.reminded',
    paid: 'dashboard.advanced_widget.invoice_status.paid',
};

// Display labels for the overdue invoice age breakdown buckets
export const INVOICE_AGE_BUCKET_LABELS: Record<string, string> = {
    '0_30': 'dashboard.advanced_widget.invoice_age_bucket.0_30',
    '31_60': 'dashboard.advanced_widget.invoice_age_bucket.31_60',
    '60_plus': 'dashboard.advanced_widget.invoice_age_bucket.60_plus',
};

// Display labels for the time row status breakdown bar buckets
export const TIME_ROW_STATUS_LABELS: Record<string, string> = {
    registrated: 'dashboard.advanced_widget.time_row_status.registrated',
    running: 'dashboard.advanced_widget.time_row_status.running',
    approved: 'dashboard.advanced_widget.time_row_status.approved',
};

// Colors for the time row status breakdown bar, matching the status badge colors used in the timesheet list
export const TIME_ROW_STATUS_COLORS: Record<string, string> = {
    registrated: 'var(--job_created)',
    running: 'var(--running_work_timer)',
    approved: 'var(--completed)',
};

// Config of every dashboard widget, grouped per domain, in catalog order
export const widgetCatalog: AdvancedWidgetConfig[] = [
    // General widgets, enabled by default for every customer
    {
        widgetKey: 'acceptedProposalValue',
        domain: 'proposals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.widget.accepted_proposal_value.title',
        query: {
            default: { endpoint: 'get_accepted_proposal_value', dataType: 'idValuePairs', valueField: 'amount' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'proposals',
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'jobsCompleted',
        domain: 'jobs',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.widget.jobs_completed.title',
        query: {
            default: { endpoint: 'get_jobs_completed_count', dataType: 'ids' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.widget.jobs_completed.measuring_unit',
        requiredFeature: 'jobs',
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'productiveHours',
        domain: 'timesheets',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.widget.productive_hours.title',
        tooltipText: 'dashboard.widget.productive_hours.tooltip_text',
        query: {
            default: { endpoint: 'get_productive_hours', dataType: 'idValuePairs', valueField: 'duration' },
            dimension: { endpoint: 'get_active_field_worker_ids', dataType: 'ids' }
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.widget.productive_hours.measuring_unit',
        decimals: 1,
        requiredFeature: ['jobs', 'manage_timesheets'],
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'revenue',
        domain: 'jobs',
        displayDomains: ['jobs', 'invoices'],
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.widget.revenue.title',
        tooltipText: 'dashboard.widget.revenue.tooltip_text',
        query: {
            default: { endpoint: 'get_dashboard_revenue', dataType: 'idValuePairs', valueField: 'revenue' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: ['invoices', 'jobs'],
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'jobStatusBreakdown',
        domain: 'jobs',
        widgetType: 'jobStatusBar',
        title: 'dashboard.chart.job_status_breakdown.title',
        endpoint: 'get_job_status_breakdown',
        requiredFeature: 'jobs',
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'jobsTrend',
        domain: 'jobs',
        widgetType: 'timeSeries',
        title: 'dashboard.chart.jobs_trend.title',
        endpoint: 'get_jobs_trend',
        requiredFeature: 'jobs',
        series: [
            { key: 'created', label: 'dashboard.chart.jobs_trend.created_series', color: 'var(--general-purple)' },
            { key: 'completed', label: 'dashboard.chart.jobs_trend.completed_series', color: 'var(--general-green)' },
        ],
        allowDecimals: false,
        defaultEnabled: true,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'focusPanelTodos',
        domain: 'general',
        widgetType: 'focusPanelTodos',
        title: 'dashboard.general.todos_today_title',
        defaultEnabled: true,
        minGridSize: { w: 2, h: 4 },
        maxGridSize: { w: 4, h: 8 },
        defaultGridSize: { w: 2, h: 4 },
    },
    {
        widgetKey: 'focusPanelActivity',
        domain: 'general',
        widgetType: 'focusPanelActivity',
        title: 'dashboard.general.activities_title',
        defaultEnabled: true,
        minGridSize: { w: 2, h: 4 },
        maxGridSize: { w: 4, h: 8 },
        defaultGridSize: { w: 2, h: 4 },
    },

    // Jobs domain, advanced widgets, off by default
    {
        widgetKey: 'jobsToScheduleRatio',
        domain: 'jobs',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.jobs_to_schedule_ratio.title',
        query: {
            default: { endpoint: 'get_jobs_to_schedule_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_jobs_currently_open_ids', dataType: 'ids' },
        },
        measuringUnit: 'dashboard.advanced_widget.jobs_to_schedule_ratio.measuring_unit',
        requiredFeature: 'jobs',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'averageHoursPerJob',
        domain: 'jobs',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.average_hours_per_job.title',
        query: {
            default: { endpoint: 'get_productive_hours', dataType: 'idValuePairs', valueField: 'duration' },
            dimension: { endpoint: 'get_jobs_with_productive_time_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.average_hours_per_job.measuring_unit',
        decimals: 1,
        requiredFeature: ['jobs', 'manage_timesheets'],
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'averageTravelTimePerJob',
        domain: 'jobs',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.average_travel_time_per_job.title',
        query: {
            default: { endpoint: 'get_travel_time', dataType: 'idValuePairs', valueField: 'duration' },
            dimension: { endpoint: 'get_jobs_with_travel_time_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.average_travel_time_per_job.measuring_unit',
        decimals: 1,
        requiredFeature: ['jobs', 'manage_timesheets'],
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'jobsByType',
        domain: 'jobs',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.jobs_by_type.title',
        tooltipText: 'dashboard.advanced_widget.jobs_by_type.tooltip_text',
        endpoint: 'get_jobs_by_type_breakdown',
        chartVariant: 'donut',
        requiredFeature: 'jobs',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'revenueByType',
        domain: 'jobs',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.revenue_by_type.title',
        tooltipText: 'dashboard.advanced_widget.revenue_by_type.tooltip_text',
        endpoint: 'get_revenue_by_type_breakdown',
        valueFormat: 'currency',
        requiredFeature: 'jobs',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'jobMargin',
        domain: 'jobs',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.job_margin.title',
        tooltipText: 'dashboard.advanced_widget.job_margin.tooltip_text',
        query: {
            default: { endpoint: 'get_job_margin', dataType: 'idValuePairs', valueField: 'margin' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'jobs',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },

    // Deals domain, advanced widgets, off by default
    {
        widgetKey: 'dealsCreated',
        domain: 'deals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.deals_created.title',
        query: {
            default: { endpoint: 'get_deals_created_ids', dataType: 'ids' }
        },
        calculationMethod: 'count',
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'dealsWinRatio',
        domain: 'deals',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.deals_win_ratio.title',
        query: {
            default: { endpoint: 'get_won_deals_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_won_or_lost_deals_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.deals_win_ratio.measuring_unit',
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'dealsPipelineValue',
        domain: 'deals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.deals_pipeline_value.title',
        query: {
            default: { endpoint: 'get_deals_pipeline_value', dataType: 'idValuePairs', valueField: 'value' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'dealsByPipelineCount',
        domain: 'deals',
        widgetType: 'statusBreakdownBar',
        title: 'dashboard.advanced_widget.deals_by_pipeline_count.title',
        endpoint: 'get_deals_by_pipeline_breakdown',
        barValueField: 'count',
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'dealsByPipelineValue',
        domain: 'deals',
        widgetType: 'statusBreakdownBar',
        title: 'dashboard.advanced_widget.deals_by_pipeline_value.title',
        endpoint: 'get_deals_by_pipeline_breakdown',
        barValueField: 'value',
        valueFormat: 'currency',
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'dealsByOwner',
        domain: 'deals',
        widgetType: 'employeeBar',
        title: 'dashboard.advanced_widget.deals_by_owner.title',
        endpoint: 'get_deals_by_owner_breakdown',
        series: [
            { key: 'value', label: 'dashboard.advanced_widget.general.count_label', color: 'var(--general-purple)' },
        ],
        requiredFeature: 'deals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },

    // Proposals domain, advanced widgets, off by default
    {
        widgetKey: 'proposalAcceptanceRatio',
        domain: 'proposals',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.proposal_acceptance_ratio.title',
        query: {
            default: { endpoint: 'get_proposals_accepted_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_proposals_accepted_or_rejected_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.proposal_acceptance_ratio.measuring_unit',
        displayAsPercentage: true,
        showComparison: true,
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'openProposalValue',
        domain: 'proposals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.open_proposal_value.title',
        query: {
            default: { endpoint: 'get_open_proposal_value', dataType: 'idValuePairs', valueField: 'amount' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'averageDaysToAcceptance',
        domain: 'proposals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.average_days_to_acceptance.title',
        query: {
            default: { endpoint: 'get_average_days_to_acceptance', dataType: 'idValuePairs', valueField: 'days' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.average_days_to_acceptance.measuring_unit',
        decimals: 1,
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'proposalsByStatusValue',
        domain: 'proposals',
        widgetType: 'statusBreakdownBar',
        title: 'dashboard.advanced_widget.proposals_by_status_value.title',
        endpoint: 'get_proposals_by_status_breakdown',
        barValueField: 'value',
        groupLabelKeys: PROPOSAL_STATUS_LABELS,
        groupColors: PROPOSAL_STATUS_COLORS,
        valueFormat: 'currency',
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'proposalsExpiringSoon',
        domain: 'proposals',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.proposals_expiring_soon.title',
        query: {
            default: { endpoint: 'get_proposals_expiring_soon_ids', dataType: 'ids' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.proposals_expiring_soon.measuring_unit',
        hideComparison: true,
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'proposalsByManagedUser',
        domain: 'proposals',
        widgetType: 'employeeBar',
        title: 'dashboard.advanced_widget.proposals_by_managed_user.title',
        endpoint: 'get_proposals_by_managed_user_breakdown',
        series: [
            { key: 'value', label: 'dashboard.advanced_widget.general.count_label', color: 'var(--general-purple)' },
        ],
        requiredFeature: 'proposals',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },

    // Orders domain, advanced widgets, off by default
    {
        widgetKey: 'ordersCreated',
        domain: 'orders',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.orders_created.title',
        query: {
            default: { endpoint: 'get_orders_created_ids', dataType: 'ids' }
        },
        calculationMethod: 'count',
        requiredFeature: 'orders',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'ordersRevenue',
        domain: 'orders',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.orders_revenue.title',
        query: {
            default: { endpoint: 'get_orders_revenue', dataType: 'idValuePairs', valueField: 'amount' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'orders',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'ordersToInvoiceValue',
        domain: 'orders',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.orders_to_invoice_value.title',
        query: {
            default: { endpoint: 'get_orders_to_invoice_value', dataType: 'idValuePairs', valueField: 'amount_to_invoice' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'orders',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'averageDaysOrderToInvoice',
        domain: 'orders',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.average_days_order_to_invoice.title',
        query: {
            default: { endpoint: 'get_average_days_order_to_invoice', dataType: 'idValuePairs', valueField: 'days' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.average_days_order_to_invoice.measuring_unit',
        decimals: 1,
        requiredFeature: 'orders',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },

    // Projects domain, advanced widgets, off by default
    {
        widgetKey: 'projectsOpenRatio',
        domain: 'projects',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.projects_open_ratio.title',
        query: {
            default: { endpoint: 'get_open_projects_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_all_projects_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.projects_open_ratio.measuring_unit',
        requiredFeature: 'projects',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'projectHoursRatio',
        domain: 'projects',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.project_hours_ratio.title',
        tooltipText: 'dashboard.advanced_widget.project_hours_ratio.tooltip_text',
        query: {
            default: { endpoint: 'get_project_actual_hours', dataType: 'idValuePairs', valueField: 'duration' },
            dimension: { endpoint: 'get_project_estimated_hours', dataType: 'idValuePairs', valueField: 'estimated_hours' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.project_hours_ratio.measuring_unit',
        requiredFeature: 'projects',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'projectsByBillingType',
        domain: 'projects',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.projects_by_billing_type.title',
        endpoint: 'get_projects_by_billing_type_breakdown',
        groupLabelKeys: PROJECT_BILLING_TYPE_LABELS,
        chartVariant: 'donut',
        requiredFeature: 'projects',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'projectMargin',
        domain: 'projects',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.project_margin.title',
        tooltipText: 'dashboard.advanced_widget.project_margin.tooltip_text',
        query: {
            default: { endpoint: 'get_project_margin', dataType: 'idValuePairs', valueField: 'margin' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'projects',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'projectCapacityVsAssigned',
        domain: 'projects',
        widgetType: 'stackedBar',
        title: 'dashboard.advanced_widget.project_capacity_vs_assigned.title',
        endpoint: 'get_project_capacity_vs_assigned',
        series: [
            { key: 'required', label: 'dashboard.advanced_widget.project_capacity_vs_assigned.required_series', color: 'var(--general-purple)' },
            { key: 'assigned', label: 'dashboard.advanced_widget.project_capacity_vs_assigned.assigned_series', color: 'var(--general-green)' },
        ],
        stackedBarStacked: false,
        requiredFeature: 'projects',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'jobsCompletedFirstVisitRatio',
        domain: 'planning',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.jobs_completed_first_visit_ratio.title',
        tooltipText: 'dashboard.advanced_widget.jobs_completed_first_visit_ratio.tooltip_text',
        query: {
            default: { endpoint: 'get_jobs_completed_first_visit_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_jobs_completed_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.jobs_completed_first_visit_ratio.measuring_unit',
        requiredFeature: 'scheduling',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'scheduledJobsByEmployee',
        domain: 'planning',
        widgetType: 'employeeBar',
        title: 'dashboard.advanced_widget.scheduled_jobs_by_employee.title',
        endpoint: 'get_scheduled_jobs_by_employee',
        series: [
            { key: 'reserved', label: JOB_STATUS_BREAKDOWN_LABELS.reserved, color: 'var(--one_reserved_appointment)' },
            { key: 'scheduled', label: JOB_STATUS_BREAKDOWN_LABELS.scheduled, color: 'var(--scheduled_appointment)' },
            { key: 'en_route', label: JOB_STATUS_BREAKDOWN_LABELS.en_route, color: 'var(--running_travel_timer)' },
            { key: 'in_progress', label: JOB_STATUS_BREAKDOWN_LABELS.in_progress, color: 'var(--running_work_timer)' },
            { key: 'incomplete', label: JOB_STATUS_BREAKDOWN_LABELS.incomplete, color: 'var(--incomplete)' },
            { key: 'completed', label: JOB_STATUS_BREAKDOWN_LABELS.completed, color: 'var(--completed)' },
        ],
        requiredFeature: 'scheduling',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'timeByType',
        domain: 'timesheets',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.time_by_type.title',
        endpoint: 'get_time_by_type_breakdown',
        chartVariant: 'donut',
        requiredFeature: 'manage_timesheets',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'travelAndWorkTimeByEmployee',
        domain: 'timesheets',
        widgetType: 'stackedBar',
        title: 'dashboard.advanced_widget.travel_and_work_time_by_employee.title',
        endpoint: 'get_travel_and_work_time_by_employee',
        series: [
            { key: 'work_time', label: 'dashboard.advanced_widget.travel_and_work_time_by_employee.work_time_series', color: 'var(--running_work_timer)' },
            { key: 'travel_time', label: 'dashboard.advanced_widget.travel_and_work_time_by_employee.travel_time_series', color: 'var(--running_travel_timer)' },
            { key: 'other_time', label: 'dashboard.advanced_widget.travel_and_work_time_by_employee.other_time_series', color: 'var(--general-gray)' },
        ],
        stackedBarUnit: 'u',
        requiredFeature: 'scheduling',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'timeByStatus',
        domain: 'timesheets',
        widgetType: 'statusBreakdownBar',
        title: 'dashboard.advanced_widget.time_by_status.title',
        endpoint: 'get_time_by_status_breakdown',
        barValueField: 'value',
        groupLabelKeys: TIME_ROW_STATUS_LABELS,
        groupColors: TIME_ROW_STATUS_COLORS,
        valueFormat: 'hours',
        requiredFeature: 'manage_timesheets',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'billableTimeRatio',
        domain: 'timesheets',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.billable_time_ratio.title',
        tooltipText: 'dashboard.advanced_widget.billable_time_ratio.tooltip_text',
        query: {
            default: { endpoint: 'get_billable_time_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_productive_time_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.billable_time_ratio.measuring_unit',
        displayAsPercentage: true,
        showComparison: true,
        requiredFeature: 'manage_timesheets',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'travelTimePercentage',
        domain: 'timesheets',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.travel_time_percentage.title',
        tooltipText: 'dashboard.advanced_widget.travel_time_percentage.tooltip_text',
        query: {
            default: { endpoint: 'get_travel_time_value', dataType: 'idValuePairs', valueField: 'duration' },
            dimension: { endpoint: 'get_total_productive_time_value', dataType: 'idValuePairs', valueField: 'duration' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.travel_time_percentage.measuring_unit',
        displayAsPercentage: true,
        showComparison: true,
        requiredFeature: 'manage_timesheets',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'timeTrend',
        domain: 'timesheets',
        widgetType: 'timeSeries',
        title: 'dashboard.advanced_widget.time_trend.title',
        endpoint: 'get_time_trend',
        series: [
            { key: 'hours', label: 'dashboard.advanced_widget.time_trend.hours_series', color: 'var(--general-purple)' },
        ],
        requiredFeature: 'manage_timesheets',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'openInvoiceAmount',
        domain: 'invoices',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.open_invoice_amount.title',
        query: {
            default: { endpoint: 'get_open_invoice_amount', dataType: 'idValuePairs', valueField: 'amount_due' }
        },
        calculationMethod: 'count',
        prefix: '€',
        requiredFeature: 'invoices',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'overdueInvoicesByAge',
        domain: 'invoices',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.overdue_invoices_by_age.title',
        tooltipText: 'dashboard.advanced_widget.overdue_invoices_by_age.tooltip_text',
        endpoint: 'get_overdue_invoices_by_age_breakdown',
        groupLabelKeys: INVOICE_AGE_BUCKET_LABELS,
        chartVariant: 'donut',
        valueFormat: 'currency',
        requiredFeature: 'invoices',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'invoicesByStatusValue',
        domain: 'invoices',
        widgetType: 'statusBreakdownBar',
        title: 'dashboard.advanced_widget.invoices_by_status_value.title',
        endpoint: 'get_invoices_by_status_breakdown',
        barValueField: 'value',
        groupLabelKeys: INVOICE_STATUS_LABELS,
        valueFormat: 'currency',
        requiredFeature: 'invoices',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 4 },
        defaultGridSize: { w: 3, h: 2 },
    },
    {
        widgetKey: 'averageDaysToPayment',
        domain: 'invoices',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.average_days_to_payment.title',
        tooltipText: 'dashboard.advanced_widget.average_days_to_payment.tooltip_text',
        query: {
            default: { endpoint: 'get_average_days_to_payment', dataType: 'idValuePairs', valueField: 'days' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.average_days_to_payment.measuring_unit',
        decimals: 1,
        requiredFeature: 'invoices',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'invoiceRevenueTrend',
        domain: 'invoices',
        widgetType: 'timeSeries',
        title: 'dashboard.advanced_widget.invoice_revenue_trend.title',
        endpoint: 'get_invoice_revenue_trend',
        series: [
            { key: 'amount', label: 'dashboard.advanced_widget.invoice_revenue_trend.amount_series', color: 'var(--general-purple)' },
        ],
        requiredFeature: 'invoices',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'openVsAllTasksRatio',
        domain: 'tasks',
        widgetType: 'number',
        variant: 'ratioWidget',
        title: 'dashboard.advanced_widget.open_vs_all_tasks_ratio.title',
        query: {
            default: { endpoint: 'get_open_tasks_ids', dataType: 'ids' },
            dimension: { endpoint: 'get_all_tasks_ids', dataType: 'ids' },
        },
        calculationMethod: 'divide',
        measuringUnit: 'dashboard.advanced_widget.open_vs_all_tasks_ratio.measuring_unit',
        requiredFeature: 'office_tasks',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'overdueTasks',
        domain: 'tasks',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.overdue_tasks.title',
        tooltipText: 'dashboard.advanced_widget.overdue_tasks.tooltip_text',
        query: {
            default: { endpoint: 'get_overdue_tasks_ids', dataType: 'ids' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.overdue_tasks.measuring_unit',
        requiredFeature: 'office_tasks',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
    {
        widgetKey: 'tasksByType',
        domain: 'tasks',
        widgetType: 'breakdown',
        title: 'dashboard.advanced_widget.tasks_by_type.title',
        endpoint: 'get_tasks_by_type_breakdown',
        chartVariant: 'donut',
        requiredFeature: 'office_tasks',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 6, h: 8 },
        defaultGridSize: { w: 2, h: 3 },
    },
    {
        widgetKey: 'ownOpenTasks',
        domain: 'tasks',
        widgetType: 'number',
        variant: 'numberWidget',
        title: 'dashboard.advanced_widget.own_open_tasks.title',
        query: {
            default: { endpoint: 'get_current_user_tasks', dataType: 'ids' }
        },
        calculationMethod: 'count',
        measuringUnit: 'dashboard.advanced_widget.own_open_tasks.measuring_unit',
        requiredFeature: 'office_tasks',
        defaultEnabled: false,
        minGridSize: { w: 1, h: 2 },
        maxGridSize: { w: 2, h: 4 },
        defaultGridSize: { w: 1, h: 2 },
    },
];
